import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Language Context for Lumiveta
 * Handles bilingual support (English / Spanish)
 * Persists language choice in localStorage
 */

const LanguageContext = createContext();

// UI Translations - COMPREHENSIVE for full Spanish toggle support
const translations = {
  en: {
    // Header & Navigation
    dashboard: 'Dashboard',
    myGoldMine: 'My Gold Mine',
    account: 'Account',
    upgrade: 'Upgrade',
    signOut: 'Sign Out',
    settings: 'Settings',
    premium: 'Premium',
    today: 'today',
    manageSubscription: 'Manage Subscription',
    adminManagement: 'Admin Management',
    
    // Main Dashboard
    welcomeBack: 'Welcome back',
    shareYourHeart: "Share what's on your heart today",
    inputPlaceholder: "What's on your heart today? Share your thoughts, struggles, or questions...",
    receiveLight: 'Receive Light',
    receiving: 'Receiving light...',
    holdToSpeak: 'Hold to speak',
    recording: 'Recording...',
    wordLimit: 'Word limit: 3,000 words',
    wordLimitError: 'Your message exceeds 3,000 words. Please shorten it.',
    whatsOnYourHeart: "What's on your heart today?",
    shareThoughts: 'Share your thoughts, struggles, or questions',
    tapToType: "Tap here to type or use your phone's microphone to speak your reflection...",
    words: 'words',
    limitExceeded: 'Limit exceeded',
    recentGold: 'Recent Gold',
    viewAll: 'View All',
    saved: 'Saved',
    quickActions: 'Quick Actions',
    yourPrivacyMatters: 'Your Privacy Matters',
    privacyDescription: 'Your reflections are private and encrypted. We never share your personal information.',
    
    // Usage & Subscription Labels
    premiumMember: 'Premium Member',
    freeTrial: 'Free Trial',
    freeTrialAccount: 'Free Trial Account',
    dailyLight: 'Daily Light',
    dailyReflections: 'daily reflections',
    dailyReflectionsUsed: 'Daily reflections used',
    reflectionsToday: 'Reflections today',
    dailyReflectionsRemaining: 'daily reflections remaining',
    subscribeToAccess: 'Subscribe to access',
    trialLimitReached: 'Trial limit reached',
    upgradeToPremium: 'Upgrade to Premium',
    viewPremiumPlans: 'View Premium Plans',
    fullPipeline: 'Full 7-part pipeline',
    fieldNotesJournaling: 'Field Notes journaling',
    renews: 'Renews',
    lowOnLight: 'Low on light!',
    reflectionRemaining: 'reflection remaining today',
    dailyLimitReached: 'Daily limit of 7 reflections reached. Try again tomorrow!',
    
    // Reflection Response
    yourReflection: 'Your Reflection',
    reflection: 'Reflection',
    theSeam: 'The Seam (Scripture)',
    theMap: 'The Map (Biblical Story)',
    fuel: 'Fuel (Encouragement)',
    footsteps: 'Footsteps (Action Steps)',
    fieldNotes: 'Field Notes (Journaling)',
    soulAnchor: 'Soul-Anchor (Grounding Exercises)',
    storeTheGold: 'Save to Gold Mine',
    stored: 'Saved!',
    listenAll: 'Listen All',
    listen: 'Listen',
    stop: 'Stop',
    share: 'Share',
    delete: 'Delete',
    backToDashboard: 'Back to Dashboard',
    lightArrived: 'Your Light Has Arrived',
    scriptureGuidance: 'Scripture-grounded guidance for your journey',
    youShared: 'You shared:',
    loading: 'Loading...',
    loadingReflection: 'Loading your reflection...',
    takeTimeToReflect: 'Take time to reflect and journal your thoughts:',
    tapHereToType: "Tap here to type or use your phone's microphone...",
    deleteReflection: 'Delete reflection?',
    cannotBeUndone: 'This action cannot be undone.',
    cancel: 'Cancel',
    deleting: 'Deleting...',
    professionalHelp: 'Remember: Professional Help is Available',
    professionalHelpDesc: "While faith-based guidance can provide comfort and direction, it's important to seek professional help when needed.",
    
    // Crisis
    crisisSupport: 'Crisis Support Available',
    crisisMessage: "We've detected you may be going through a difficult time.",
    viewCrisisResources: 'View Crisis Resources',
    
    // History / Gold Mine
    goldMineTitle: 'My Gold Mine',
    goldMineSubtitle: 'Your saved reflections and spiritual treasures',
    searchPlaceholder: 'Search your reflections...',
    noReflections: 'No reflections yet',
    startJourney: 'Start your journey by sharing what\'s on your heart',
    
    // Auth
    signIn: 'Sign In',
    createAccount: 'Create Account',
    forgotPassword: 'Forgot Password?',
    email: 'Email',
    password: 'Password',
    name: 'Name',
    fullName: 'Full Name',
    emailAddress: 'Email Address',
    emailCannotBeChanged: 'Email cannot be changed',
    
    // Account Settings
    profile: 'Profile',
    security: 'Security',
    subscription: 'Subscription',
    dataPrivacy: 'Data & Privacy',
    personalInfo: 'Personal Information',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    saveChanges: 'Save Changes',
    saving: 'Saving...',
    updatePassword: 'Update Password',
    updating: 'Updating...',
    profileUpdated: 'Profile updated successfully',
    passwordUpdated: 'Password updated successfully',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordMinLength: 'Password must be at least 8 characters with a number',
    
    // Subscription Plans
    choosePlan: 'Choose Your Plan',
    trialPlan: '7-Day Free Trial',
    monthlyPlan: 'Monthly',
    yearlyPlan: 'Yearly',
    perMonth: '/month',
    perYear: '/year',
    startTrial: 'Start Free Trial',
    subscribe: 'Subscribe',
    mostPopular: 'Most Popular',
    bestValue: 'Best Value',
    features: 'Features',
    
    // Daily Reminders / Notifications
    dailyReminders: 'Daily Reminders',
    enableNotifications: 'Enable Notifications',
    notificationsEnabled: 'Notifications enabled',
    notificationsDisabled: 'Notifications disabled',
    notificationsNotSupported: 'Push notifications are not supported in this browser',
    
    // Data & Privacy
    deleteAccount: 'Delete Account',
    deleteAccountWarning: 'This will permanently delete your account and all your data.',
    confirmDelete: 'Confirm Delete',
    
    // Language
    language: 'Language',
    english: 'English',
    spanish: 'Español',
    
    // Footer
    copyright: 'Light for your path.',
    
    // Login Page
    lightForYourPath: 'Light for your path',
    findBiblicalGuidance: 'Find biblical guidance, encouragement, and peace through AI-powered spiritual mentorship grounded in scripture.',
    scriptureGrounded: 'Scripture-Grounded',
    scriptureGroundedDesc: 'Every response rooted in World English Bible (WEB) and Spanish Standard Edition (SSE)',
    sevenPartResponse: '7-Part Response',
    sevenPartResponseDesc: 'Reflection, Verse, Story, Fuel, Footsteps, Field Notes, Soul-Anchor',
    safeAndPrivate: 'Safe & Private',
    safeAndPrivateDesc: 'Your reflections are encrypted and never shared',
    signInToContinue: 'Sign in to continue your spiritual journey',
    signingIn: 'Signing In...',
    dontHaveAccount: "Don't have an account?",
    adminMasterRecovery: 'Admin Master Recovery',
    masterRecoveryWarning: 'Master Recovery is for emergency admin access only. This action is logged.',
    masterRecoveryKey: 'Master Recovery Key',
    verifying: 'Verifying...',
    accessAdmin: 'Access Admin',
    backToLogin: '← Back to Login',
    copyrightFull: '© 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™',
  },
  es: {
    // Header & Navigation
    dashboard: 'Panel',
    myGoldMine: 'Mi Mina de Oro',
    account: 'Cuenta',
    upgrade: 'Mejorar',
    signOut: 'Cerrar Sesión',
    settings: 'Configuración',
    premium: 'Premium',
    today: 'hoy',
    manageSubscription: 'Gestionar Suscripción',
    adminManagement: 'Gestión de Admin',
    
    // Main Dashboard
    welcomeBack: 'Bienvenido de nuevo',
    shareYourHeart: 'Comparte lo que hay en tu corazón hoy',
    inputPlaceholder: '¿Qué hay en tu corazón hoy? Comparte tus pensamientos, luchas o preguntas...',
    receiveLight: 'Recibir Luz',
    receiving: 'Recibiendo luz...',
    holdToSpeak: 'Mantén presionado para hablar',
    recording: 'Grabando...',
    wordLimit: 'Límite: 3,000 palabras',
    wordLimitError: 'Tu mensaje excede 3,000 palabras. Por favor acórtalo.',
    whatsOnYourHeart: '¿Qué hay en tu corazón hoy?',
    shareThoughts: 'Comparte tus pensamientos, luchas o preguntas',
    tapToType: 'Toca aquí para escribir o usa el micrófono de tu teléfono para dictar tu reflexión...',
    words: 'palabras',
    limitExceeded: 'Límite excedido',
    recentGold: 'Oro Reciente',
    viewAll: 'Ver Todo',
    saved: 'Guardado',
    quickActions: 'Acciones Rápidas',
    yourPrivacyMatters: 'Tu Privacidad Importa',
    privacyDescription: 'Tus reflexiones son privadas y encriptadas. Nunca compartimos tu información personal.',
    
    // Usage & Subscription Labels
    premiumMember: 'Miembro Premium',
    freeTrial: 'Prueba Gratuita',
    freeTrialAccount: 'Cuenta de Prueba Gratuita',
    dailyLight: 'Luz Diaria',
    dailyReflections: 'reflexiones diarias',
    dailyReflectionsUsed: 'Reflexiones diarias usadas',
    reflectionsToday: 'Reflexiones hoy',
    dailyReflectionsRemaining: 'reflexiones diarias restantes',
    subscribeToAccess: 'Suscríbete para acceder',
    trialLimitReached: 'Límite de prueba alcanzado',
    upgradeToPremium: 'Actualizar a Premium',
    viewPremiumPlans: 'Ver Planes Premium',
    fullPipeline: 'Pipeline completo de 7 partes',
    fieldNotesJournaling: 'Diario de Notas de Campo',
    renews: 'Renueva',
    lowOnLight: '¡Poca luz!',
    reflectionRemaining: 'reflexión restante hoy',
    dailyLimitReached: '¡Límite diario de 7 reflexiones alcanzado. Vuelve mañana!',
    
    // Reflection Response
    yourReflection: 'Tu Reflexión',
    reflection: 'Reflexión Profunda',
    theSeam: 'La Palabra Viva (Escritura)',
    theMap: 'La Historia (Relato Bíblico)',
    fuel: 'Combustible para el Alma (Ánimo)',
    footsteps: 'Pasos a Seguir (Acciones)',
    fieldNotes: 'Notas de Campo (Diario)',
    soulAnchor: 'El Ancla del Alma (Ejercicios de Anclaje)',
    storeTheGold: 'Guardar en Mina de Oro',
    stored: '¡Guardado!',
    listenAll: 'Escuchar Todo',
    listen: 'Escuchar',
    stop: 'Detener',
    share: 'Compartir',
    delete: 'Eliminar',
    backToDashboard: 'Volver al Panel',
    lightArrived: 'Tu Luz Ha Llegado',
    scriptureGuidance: 'Guía fundamentada en las Escrituras para tu camino',
    youShared: 'Compartiste:',
    loading: 'Cargando...',
    loadingReflection: 'Cargando tu reflexión...',
    takeTimeToReflect: 'Toma tiempo para reflexionar y escribir tus pensamientos:',
    tapHereToType: 'Toca aquí para escribir o usa el micrófono de tu teléfono...',
    deleteReflection: '¿Eliminar reflexión?',
    cannotBeUndone: 'Esta acción no se puede deshacer.',
    cancel: 'Cancelar',
    deleting: 'Eliminando...',
    professionalHelp: 'Recuerda: Hay Ayuda Profesional Disponible',
    professionalHelpDesc: 'Aunque la guía basada en la fe puede proporcionar consuelo y dirección, es importante buscar ayuda profesional cuando sea necesario.',
    
    // Crisis
    crisisSupport: 'Apoyo en Crisis Disponible',
    crisisMessage: 'Detectamos que podrías estar pasando por un momento difícil.',
    viewCrisisResources: 'Ver Recursos de Crisis',
    
    // History / Gold Mine
    goldMineTitle: 'Mi Mina de Oro',
    goldMineSubtitle: 'Tus reflexiones guardadas y tesoros espirituales',
    searchPlaceholder: 'Buscar tus reflexiones...',
    noReflections: 'Aún no hay reflexiones',
    startJourney: 'Comienza tu camino compartiendo lo que hay en tu corazón',
    
    // Auth
    signIn: 'Iniciar Sesión',
    createAccount: 'Crear Cuenta',
    forgotPassword: '¿Olvidaste tu Contraseña?',
    email: 'Correo Electrónico',
    password: 'Contraseña',
    name: 'Nombre',
    fullName: 'Nombre Completo',
    emailAddress: 'Correo Electrónico',
    emailCannotBeChanged: 'El correo no se puede cambiar',
    
    // Account Settings
    profile: 'Perfil',
    security: 'Seguridad',
    subscription: 'Suscripción',
    dataPrivacy: 'Datos y Privacidad',
    personalInfo: 'Información Personal',
    changePassword: 'Cambiar Contraseña',
    currentPassword: 'Contraseña Actual',
    newPassword: 'Nueva Contraseña',
    confirmNewPassword: 'Confirmar Nueva Contraseña',
    saveChanges: 'Guardar Cambios',
    saving: 'Guardando...',
    updatePassword: 'Actualizar Contraseña',
    updating: 'Actualizando...',
    profileUpdated: 'Perfil actualizado exitosamente',
    passwordUpdated: 'Contraseña actualizada exitosamente',
    passwordsDoNotMatch: 'Las contraseñas no coinciden',
    passwordMinLength: 'La contraseña debe tener al menos 6 caracteres',
    
    // Subscription Plans
    choosePlan: 'Elige Tu Plan',
    trialPlan: 'Prueba Gratuita de 7 Días',
    monthlyPlan: 'Mensual',
    yearlyPlan: 'Anual',
    perMonth: '/mes',
    perYear: '/año',
    startTrial: 'Comenzar Prueba Gratuita',
    subscribe: 'Suscribirse',
    mostPopular: 'Más Popular',
    bestValue: 'Mejor Valor',
    features: 'Características',
    
    // Daily Reminders / Notifications
    dailyReminders: 'Recordatorios Diarios',
    enableNotifications: 'Activar Notificaciones',
    notificationsEnabled: 'Notificaciones activadas',
    notificationsDisabled: 'Notificaciones desactivadas',
    notificationsNotSupported: 'Las notificaciones push no son compatibles con este navegador',
    
    // Data & Privacy
    deleteAccount: 'Eliminar Cuenta',
    deleteAccountWarning: 'Esto eliminará permanentemente tu cuenta y todos tus datos.',
    confirmDelete: 'Confirmar Eliminación',
    
    // Language
    language: 'Idioma',
    english: 'English',
    spanish: 'Español',
    
    // Footer
    copyright: 'Luz para tu camino.',
    
    // Login Page
    lightForYourPath: 'Luz para tu camino',
    findBiblicalGuidance: 'Encuentra guía bíblica, ánimo y paz a través de mentoría espiritual potenciada por IA y fundamentada en las Escrituras.',
    scriptureGrounded: 'Fundamentado en las Escrituras',
    scriptureGroundedDesc: 'Cada respuesta basada en la World English Bible (WEB) y la Versión Estándar Española (SSE)',
    sevenPartResponse: 'Respuesta de 7 Partes',
    sevenPartResponseDesc: 'Reflexión, Versículo, Historia, Combustible, Pasos, Notas de Campo, Ancla del Alma',
    safeAndPrivate: 'Seguro y Privado',
    safeAndPrivateDesc: 'Tus reflexiones están encriptadas y nunca se comparten',
    signInToContinue: 'Inicia sesión para continuar tu camino espiritual',
    signingIn: 'Iniciando sesión...',
    dontHaveAccount: '¿No tienes una cuenta?',
    adminMasterRecovery: 'Recuperación Maestra de Admin',
    masterRecoveryWarning: 'La Recuperación Maestra es solo para acceso de emergencia de admin. Esta acción queda registrada.',
    masterRecoveryKey: 'Clave de Recuperación Maestra',
    verifying: 'Verificando...',
    accessAdmin: 'Acceder como Admin',
    backToLogin: '← Volver al Login',
    copyrightFull: '© 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™',
  }
};

// Spanish detection patterns
const spanishPatterns = [
  /\b(hola|gracias|por favor|ayuda|necesito|siento|estoy|tengo|quiero|puedo|dios|señor|padre|jesús|cristo|biblia|oración|fe|esperanza|amor|paz|bendición)\b/i,
  /[áéíóúüñ¿¡]/,
  /\b(el|la|los|las|un|una|unos|unas|de|del|al|en|con|por|para|sobre|entre)\b/i
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('lumiveta_language') || 'en';
  });
  const [isAutoDetected, setIsAutoDetected] = useState(false);

  // Save language preference
  useEffect(() => {
    localStorage.setItem('lumiveta_language', language);
  }, [language]);

  // Get translation
  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  }, [language]);

  // Toggle language
  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
    setIsAutoDetected(false);
  }, []);

  // Set specific language
  const setLang = useCallback((lang) => {
    if (lang === 'en' || lang === 'es') {
      setLanguage(lang);
      setIsAutoDetected(false);
    }
  }, []);

  // Auto-detect Spanish from text input
  const detectLanguage = useCallback((text) => {
    if (!text || text.length < 5) return null;
    
    const isSpanish = spanishPatterns.some(pattern => pattern.test(text));
    
    if (isSpanish && language !== 'es') {
      setLanguage('es');
      setIsAutoDetected(true);
      return 'es';
    }
    
    return language;
  }, [language]);

  // Get speech recognition language code
  const getSpeechLang = useCallback(() => {
    return language === 'es' ? 'es-ES' : 'en-US';
  }, [language]);

  // Get TTS voice preference - STRICTLY prioritizes HIGH-QUALITY premium voices
  // BILINGUAL: Forces premium voice selection for BOTH English AND Spanish
  const getTTSVoice = useCallback((voices) => {
    if (!voices || voices.length === 0) return null;
    
    // CRITICAL: Premium voice priority lists - HIGH QUALITY voices ONLY
    // Spanish: NEVER use default English voice for Spanish text
    // English: NEVER use robotic/low-quality voices
    const preferredVoices = language === 'es' 
      ? [
          // TOP TIER: Google premium Spanish voices (best quality)
          'Google español', 'Google español de Estados Unidos', 'Google Spanish',
          // iOS/macOS premium Spanish voices
          'Mónica', 'Monica', 'Paulina', 'Jorge', 'Diego', 'Juan',
          // Microsoft premium Spanish voices
          'Microsoft Helena', 'Microsoft Laura', 'Microsoft Pablo',
          'Microsoft Sabina', 'Microsoft Raul',
          // Android voices
          'Spanish (United States)', 'Spanish (Mexico)', 'Spanish (Spain)',
          // Language code fallbacks - MUST match Spanish
          'es-MX', 'es-US', 'es-ES', 'es-419'
        ]
      : [
          // TOP TIER: Google premium English voices (best quality)
          'Google US English', 'Google UK English Female', 'Google UK English Male',
          // iOS/macOS premium English voices
          'Samantha', 'Alex', 'Karen', 'Daniel', 'Moira', 'Tessa', 'Fiona',
          // Microsoft premium English voices
          'Microsoft Zira', 'Microsoft David', 'Microsoft Mark',
          'Microsoft Jenny', 'Microsoft Guy',
          // Android voices
          'English (United States)', 'English (United Kingdom)',
          // Language code fallbacks
          'en-US', 'en-GB', 'en-AU'
        ];
    
    // First pass: Find EXACT match for premium voices (highest quality)
    for (const pref of preferredVoices) {
      const voice = voices.find(v => v.name === pref);
      if (voice) {
        console.log(`[TTS] Selected premium voice: ${voice.name} (${voice.lang})`);
        return voice;
      }
    }
    
    // Second pass: Find voice NAME containing preference
    for (const pref of preferredVoices) {
      const voice = voices.find(v => v.name.includes(pref));
      if (voice) {
        console.log(`[TTS] Selected voice (partial match): ${voice.name} (${voice.lang})`);
        return voice;
      }
    }
    
    // Third pass: Find by language code - CRITICAL for bilingual support
    const langCode = language === 'es' ? 'es' : 'en';
    for (const pref of preferredVoices) {
      const voice = voices.find(v => v.lang && v.lang.includes(pref));
      if (voice) {
        console.log(`[TTS] Selected voice (lang match): ${voice.name} (${voice.lang})`);
        return voice;
      }
    }
    
    // STRICT fallback: Only use voice matching the current language
    const matchingVoice = voices.find(v => v.lang && v.lang.startsWith(langCode));
    if (matchingVoice) {
      console.log(`[TTS] Fallback voice: ${matchingVoice.name} (${matchingVoice.lang})`);
      return matchingVoice;
    }
    
    // Last resort - log warning if using wrong language voice
    console.warn(`[TTS] WARNING: No ${language === 'es' ? 'Spanish' : 'English'} voice found!`);
    return voices[0];
  }, [language]);

  const value = {
    language,
    setLanguage: setLang,
    toggleLanguage,
    t,
    detectLanguage,
    isAutoDetected,
    getSpeechLang,
    getTTSVoice,
    isSpanish: language === 'es',
    isEnglish: language === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
